import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  DateRangeArgs,
  EmployeePayrollTotalsArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs
} from '../../../../models/reports';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { PayrollTotals } from '../../../../models/client';
import { TimeframeOptions } from '../../report-enums';
import { CompaniesService } from '../../../../services/companies.service';
import {
  dateRangeComponentValidator,
  dateRangeRequired,
  dateRangeValidator
} from '../../../../utils/dateRangeValidator';
import moment from 'moment';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../utils/datepicker-value-parser';
import { GetQuarterEndFromDate, GetQuarterStartFromDate } from '../../../../utils/quarter-utils';
import { DownloadFormatOption } from '../report-buttons/report-buttons.component';

@Component({
  selector: 'lib-labor-detail-with-totals',
  templateUrl: './labor-detail-with-totals.component.html',
  styleUrls: ['./labor-detail-with-totals.component.css'],
  standalone: false
})
export class LaborDetailWithTotalsComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public formats: DownloadFormatOption[] = [DownloadFormatOption.CSV()];
  public reportGenerationForm: UntypedFormGroup;

  private readonly ENDPOINT = 'LaborDetailWithDepartmentTotals';

  private lastPayrollTotals: PayrollTotals;

  public timeframeOptions = [
    { value: TimeframeOptions.CurrentPayroll, name: 'Current Payroll' },
    { value: TimeframeOptions.MonthToDate, name: 'MTD' },
    { value: TimeframeOptions.QuarterToDate, name: 'QTD' },
    { value: TimeframeOptions.YearToDate, name: 'YTD' },
    { value: TimeframeOptions.DateRange, name: 'Date Range' }
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService
  ) {}

  ngOnInit(): void {
    this.reportGenerationForm = this.fb.group(
      {
        timeframe: this.fb.control(null, Validators.required),
        dateRange: this.fb.control({ start: null, end: null }, [
          dateRangeRequired,
          dateRangeComponentValidator
        ])
      },
      { validators: [sameYearValidator] }
    );
    this.companiesService.getLastPayrollTotals(this.clientCode, 0).subscribe(result => {
      this.lastPayrollTotals = result.length > 0 ? result[0] : null;
    });
    this.reportGenerationForm
      .get('timeframe')
      .valueChanges.subscribe(val => this._timeframeChanged(val));
  }
  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const formattedStartDate = moment(args.StartDate).format('MM-DD-YYYY');
    const formattedEndDate = moment(args.EndDate).format('MM-DD-YYYY');
    const now = moment().format('MMDDYYYY');
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Labor Detail with Department Totals_${this.clientCode}_${formattedStartDate} to ${formattedEndDate}_${now}.${format}`,
      format
    });
  }

  private _getModel() {
    const args: DateRangeArgs = {
      StartDate: this.reportGenerationForm.get('dateRange').value.start,
      EndDate: this.reportGenerationForm.get('dateRange').value.end
    };
    return args;
  }

  private _timeframeChanged(val: TimeframeOptions) {
    const dateRangeControl = this.reportGenerationForm.get('dateRange');
    const now = new Date();
    if (val == TimeframeOptions.CurrentPayroll) {
      dateRangeControl.setValue({
        start: this.lastPayrollTotals.PayDate,
        end: this.lastPayrollTotals.PayDate
      });
      dateRangeControl.disable();
    } else if (val == TimeframeOptions.MonthToDate) {
      dateRangeControl.setValue({
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
      });
      dateRangeControl.disable();
    } else if (val == TimeframeOptions.QuarterToDate) {
      dateRangeControl.setValue({
        start: GetQuarterStartFromDate(now),
        end: GetQuarterEndFromDate(now)
      });
      dateRangeControl.disable();
    } else if (val == TimeframeOptions.YearToDate) {
      dateRangeControl.setValue({
        start: new Date(now.getFullYear(), 0, 1),
        end: new Date(now.getFullYear(), 11, 31)
      });
      dateRangeControl.disable();
    } else if (val == TimeframeOptions.DateRange) {
      dateRangeControl.setValue({ start: new Date(now.getFullYear(), 0, 1), end: now });
      dateRangeControl.enable();
    }
    dateRangeControl.updateValueAndValidity();
  }
}

export const sameYearValidator: ValidatorFn = (control: AbstractControl) => {
  let start = control.get('dateRange').value?.start;
  let end = control.get('dateRange').value?.end;
  if (start == null || end == null) {
    return null;
  }
  if (typeof start === 'string') {
    start = new Date(start);
  }
  if (typeof end === 'string') {
    end = new Date(end);
  }
  if (start.getFullYear() != end.getFullYear()) {
    return { mustBeSameYear: true };
  }
  return null;
};
